import { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, FormControl, FormLabel, Input, Stack, Switch, Typography } from '@mui/joy';
import { LayoutContext } from '../../context';
import { APIGetWorkCalendars, APISaveWorkCalendar } from '../../api';
import { getAccessToken } from '../../auth';

export default function CalendarSettings() {
  const { token, account, currentDoctor } = useContext(LayoutContext);
  const actor = account || currentDoctor;
  const allowed = actor?.type_profile?.some((r) => ['admin', 'moderator', 'supervisor'].includes(r));
  const [row, setRow] = useState(null);
  const [ready, setReady] = useState(false);
  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const epoch = useRef(0);
  const saving = useRef(false);
  useEffect(() => {
    const generation = ++epoch.current;
    setRow(null); setReady(false); setUrl(''); setEnabled(true); setBusy(false);
    setError(''); setMessage(''); saving.current = false;
    if (!allowed) return;
    let loading = false;
    let initialized = false;
    const load = async () => {
      if (loading) return;
      loading = true;
      try {
        const data = await APIGetWorkCalendars(getAccessToken() || token);
        if (epoch.current !== generation) return;
        const duty = (data.calendars || []).find((r) => r.kind === 'duty') || null;
        setRow(duty); setReady(true);
        if (!initialized) { setEnabled(duty?.enabled ?? true); initialized = true; }
      } catch {
        if (epoch.current === generation) setError('Не удалось обновить данные общего календаря');
      } finally { loading = false; }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { epoch.current++; clearInterval(timer); };
  }, [allowed, actor?.id, token]);
  if (!allowed) return null;
  const save = async () => {
    if (!ready || saving.current) return;
    if (!url.trim() && !row?.has_feed) { setError('Вставьте ссылку iCal'); return; }
    const generation = epoch.current;
    saving.current = true; setBusy(true); setError(''); setMessage('');
    try {
      const result = await APISaveWorkCalendar(getAccessToken() || token, {
        kind: 'duty', feed_url: url.trim(), timezone: row?.timezone || 'Europe/Moscow', enabled });
      if (epoch.current !== generation) return;
      setUrl(''); setMessage(result.message);
      const data = await APIGetWorkCalendars(getAccessToken() || token);
      if (epoch.current === generation) setRow((data.calendars || []).find((r) => r.kind === 'duty') || null);
    } catch (e) {
      if (epoch.current === generation) setError(e?.response?.data?.error || 'Не удалось сохранить общий календарь');
    } finally {
      if (epoch.current === generation) { saving.current = false; setBusy(false); }
    }
  };
  return <Box sx={{ p: 2, mb: 3, borderRadius: 'lg', bgcolor: 'background.surface' }}>
    <Stack spacing={1.5}>
      <Typography level="title-lg">Общий график дежурного врача</Typography>
      <Typography level="body-sm">Используется только в дежурном чате. Персональные календари настраиваются в карточках врачей: Пользователи → Врачи.</Typography>
      {error && <Alert color="danger">{error}</Alert>}
      {message && <Alert color="success">{message}</Alert>}
      <FormControl><FormLabel>Ссылка iCal общего календаря</FormLabel>
        <Input type="password" autoComplete="new-password" disabled={!ready || busy} value={url}
          onChange={(e) => setUrl(e.target.value)} placeholder={row?.has_feed ? 'Пустое поле сохраняет текущую ссылку' : 'Вставьте ссылку экспорта iCal'} />
      </FormControl>
      <FormControl orientation="horizontal"><FormLabel>Показывать общий график</FormLabel>
        <Switch disabled={!ready || busy} checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
      </FormControl>
      <Button disabled={!ready} loading={busy} onClick={save}>Сохранить общий календарь</Button>
      <Typography level="body-xs">{row?.last_error ? 'Ошибка синхронизации: проверьте ссылку и журнал сервера.'
        : row?.synced_at ? 'Общий календарь синхронизирован. Повторная проверка выполняется каждый час.' : 'Ожидает синхронизации.'}</Typography>
    </Stack>
  </Box>;
}